import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "react-bootstrap";
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    closestCenter,
    pointerWithin,
    useSensor,
    useSensors,
    type CollisionDetection,
    type Collision,
    type DragCancelEvent,
    type DragEndEvent,
    type DragStartEvent,
} from "@dnd-kit/core";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import "./styles.scss";
import ContentCatalog from "./contentCatalog";
import type { AdMenuItemsFn, FolderMenuItemsFn } from "./contentCatalog";
import { contentCatalogStore } from "./contentCatalogStore";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import { CoreService } from "@services/core/CoreService";
import { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import { Ad } from "@models/Ad.model";
import CreateEditorModal from "../createEditor/createEditor";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { BrandAssets, FontVariant } from "@models/brand.model";
import StoreSettingsModal from "./components/StoreSettingsModal";
import RenameActionModal from "./components/RenameActionModal";
import ConfirmActionModal from "./components/ConfirmActionModal";
import { contentCatalogEvents } from "./contentCatalogEvents";
import {
    handleCatalogDragEnd,
    isCatalogDragItem,
    clearCatalogPointerPosition,
    setCatalogPointerPosition,
    type CatalogPersistAction,
    type CatalogSection,
} from "./contentCatalogDnd";
import { appStore } from "@store/appStore";
import { PermissionLevels } from "@models/permission.model";
import { MediaService } from "@services/media/MediaService";
import { collectLeafAds, zipAndDownload } from "@/shared/utils/zipAndDownload";
import OrganizationPicker from "@/shared/components/OrganizationPicker/OrganizationPicker";

// ─── Tree helpers ───

function toFolderModel(raw: Partial<ContentCatalogFolder> | undefined): ContentCatalogFolder {
    const folder = new ContentCatalogFolder();
    Object.assign(folder, raw);
    folder.children = Array.isArray(raw?.children)
        ? raw!.children.map((child) => toFolderModel(child))
        : [];
    folder.ads = Array.isArray(raw?.ads) ? raw!.ads.map((ad) => new Ad(ad)) : [];
    return folder;
}

function findFolderById(nodes: ContentCatalogFolder[], folderId: number): ContentCatalogFolder | null {
    for (const node of nodes) {
        if (node.id === folderId) return node;
        const found = findFolderById(node.children || [], folderId);
        if (found) return found;
    }
    return null;
}

function removeFolderById(nodes: ContentCatalogFolder[], folderId: number): boolean {
    const index = nodes.findIndex((node) => node.id === folderId);
    if (index >= 0) {
        nodes.splice(index, 1);
        return true;
    }
    for (const node of nodes) {
        if (removeFolderById(node.children || [], folderId)) return true;
    }
    return false;
}

function removeAdFromFolder(nodes: ContentCatalogFolder[], folderId: number, adId: number): boolean {
    const folder = findFolderById(nodes, folderId);
    if (!folder) return false;
    const originalLength = folder.ads.length;
    folder.ads = folder.ads.filter((ad) => ad.id !== adId);
    return folder.ads.length !== originalLength;
}

function removeAdFromTree(nodes: ContentCatalogFolder[], adId: number): boolean {
    let removed = false;
    for (const node of nodes) {
        const originalLength = node.ads.length;
        node.ads = node.ads.filter((ad) => ad.id !== adId);
        if (node.ads.length !== originalLength) removed = true;
        if (removeAdFromTree(node.children || [], adId)) removed = true;
    }
    return removed;
}

function renameAdInTree(nodes: ContentCatalogFolder[], adId: number, nextName: string): boolean {
    let changed = false;
    for (const node of nodes) {
        for (const ad of node.ads) {
            if (ad.id === adId) {
                ad.name = nextName;
                changed = true;
            }
        }
        if (renameAdInTree(node.children || [], adId, nextName)) changed = true;
    }
    return changed;
}

function renameFolderInTree(nodes: ContentCatalogFolder[], folderId: number, nextName: string): boolean {
    for (const node of nodes) {
        if (node.id === folderId) {
            node.name = nextName;
            return true;
        }
        if (renameFolderInTree(node.children || [], folderId, nextName)) return true;
    }
    return false;
}

function findAdById(nodes: ContentCatalogFolder[], adId: number): Ad | null {
    for (const folder of nodes) {
        const found = (folder.ads || []).find((ad) => ad.id === adId);
        if (found) return found;
        const foundInChildren = findAdById(folder.children || [], adId);
        if (foundInChildren) return foundInChildren;
    }
    return null;
}

// ─── Types ───

type PendingRename =
    | { kind: "folder"; folderId: number; section: CatalogSection; currentName: string }
    | { kind: "ad"; adId: number; currentName: string };

type PendingDelete =
    | { kind: "folder"; folderId: number; section: CatalogSection; name: string }
    | { kind: "ad"; adId: number; parentFolderId: number; section: CatalogSection; name: string };

// ─── Component ───

export default function ContentCatalogPage() {
    useDocumentTitle("Content Catalog");
    const [searchParams, setSearchParams] = useSearchParams();
    const ccService = useMemo(() => new ContentCatalogService(), []);
    const coreService = useMemo(() => new CoreService(), []);
    const mediaService = useMemo(() => new MediaService(), []);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOverlayLabel, setDragOverlayLabel] = useState("");
    const [editingAdId, setEditingAdId] = useState<number | null>(() => {
        const param = searchParams.get("adId");
        return param ? Number(param) : null;
    });
    const [isEditorOpen, setIsEditorOpen] = useState(() => searchParams.has("adId"));
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [pendingMutation, setPendingMutation] = useState(false);
    const [pendingRename, setPendingRename] = useState<PendingRename | null>(null);
    const [renaming, setRenaming] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
    const [orgBrandFonts, setOrgBrandFonts] = useState<FontVariant[]>([]);
    const [organizationId, setOrganizationId] = useState<number>(appStore.loggedInUser.organization.id);

    const hasAccess = (feature: string, level: number = PermissionLevels.VIEW) =>
        appStore.loggedInUser?.permissions?.hasAccess?.(feature, level) ?? false;

    const canUseCatalogSelector = hasAccess("Catalog Selector");

    const canCreate = hasAccess("Content Catalog", PermissionLevels.CREATE);
    const canUpdate = hasAccess("Content Catalog", PermissionLevels.UPDATE);
    const canDelete = hasAccess("Content Catalog", PermissionLevels.DELETE);

    const brandAssets = useMemo<BrandAssets>(() => ({
        colors: [],
        images: [],
        fonts: orgBrandFonts,
    }), [orgBrandFonts]);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
    );

    // ─── Pointer tracking ───

    const getPointerPosition = useCallback((event: Event | null): { x: number; y: number } | null => {
        if (!event) return null;
        if (event instanceof MouseEvent || event instanceof PointerEvent) {
            return { x: event.clientX, y: event.clientY };
        }
        if (event instanceof TouchEvent) {
            const touch = event.touches[0] || event.changedTouches[0];
            if (touch) return { x: touch.clientX, y: touch.clientY };
        }
        return null;
    }, []);

    useEffect(() => {
        if (!isDragging) {
            clearCatalogPointerPosition();
            return;
        }

        let rafId = 0;
        let latestX = 0;
        let latestY = 0;

        const handlePointerMove = (event: PointerEvent) => {
            latestX = event.clientX;
            latestY = event.clientY;
            if (!rafId) {
                rafId = requestAnimationFrame(() => {
                    setCatalogPointerPosition({ x: latestX, y: latestY });
                    rafId = 0;
                });
            }
        };

        window.addEventListener("pointermove", handlePointerMove, { passive: true });
        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            if (rafId) cancelAnimationFrame(rafId);
            clearCatalogPointerPosition();
        };
    }, [isDragging]);

    // ─── DnD collision ───

    const prioritizeCollisions = useCallback(
        (collisions: Collision[], args: Parameters<CollisionDetection>[0]): Collision[] => {
            if (collisions.length <= 1) return collisions;

            const getContainerById = (id: string | number) => {
                const containers = args.droppableContainers as any;
                if (Array.isArray(containers)) {
                    return containers.find((container) => container?.id === id);
                }
                if (containers && typeof containers.get === "function") {
                    return containers.get(id);
                }
                if (containers && typeof containers.getEnabled === "function") {
                    return containers.getEnabled().find((container: any) => container?.id === id);
                }
                return undefined;
            };

            const priorityOrder = ["folder-item", "ad-item", "ad-container", "folder-container"];
            for (const kind of priorityOrder) {
                const matches = collisions.filter((collision) => {
                    const container = getContainerById(collision.id);
                    const dataKind = container?.data.current?.kind;
                    return dataKind === kind;
                });
                if (matches.length > 0) return matches;
            }

            return collisions;
        },
        [],
    );

    const collisionDetection = useCallback<CollisionDetection>((args) => {
        const pointerCollisions = pointerWithin(args);
        if (pointerCollisions.length > 0) {
            return prioritizeCollisions(pointerCollisions, args);
        }
        const centerCollisions = closestCenter(args);
        return prioritizeCollisions(centerCollisions, args);
    }, [prioritizeCollisions]);

    // ─── Reload ───

    const reloadAbortRef = useRef<AbortController | null>(null);

    // Switching organizations cancels an in-flight rollback reload, otherwise its
    // response resolves late and overwrites the newly selected org's catalog.
    useEffect(() => {
        return () => reloadAbortRef.current?.abort();
    }, [organizationId]);

    const reloadCatalogFromApi = useCallback(async () => {
        reloadAbortRef.current?.abort();
        const controller = new AbortController();
        reloadAbortRef.current = controller;

        const [collections, folders, tagGroups] = await Promise.all([
            ccService.getFolders(organizationId, undefined, true, "collection", controller.signal),
            ccService.getFolders(organizationId, undefined, true, "folder", controller.signal),
            // Unscoped: the filter taxonomy is the same on every catalog (MPN-1579).
            coreService.getTagGroups(undefined, controller.signal),
        ]);
        // A response that landed just before the abort must not publish either.
        if (controller.signal.aborted) return;

        contentCatalogStore.folders = folders;
        contentCatalogStore.collections = collections;
        contentCatalogStore.tagGroups = tagGroups;
        contentCatalogEvents.send_event("CONTENT_LOADED");
    }, [ccService, coreService, organizationId]);

    useEffect(() => {
        // getOrganizationBrand takes no signal, so this only drops the stale
        // response rather than cancelling the request.
        let superseded = false;
        async function loadOrgBrandFonts() {
            try {
                const orgService = new OrganizationService();
                const orgBrand = await orgService.getOrganizationBrand(String(organizationId));
                if (superseded) return;
                if (orgBrand?.fonts) setOrgBrandFonts(orgBrand.fonts);
            } catch {
                // Org brand font fetch is non-critical
            }
        }
        loadOrgBrandFonts();
        return () => { superseded = true; };
    }, [organizationId]);

    // ─── DnD persistence ───

    const persistCatalogAction = useCallback(async (action: CatalogPersistAction) => {
        if (action.kind === "folder-move") {
            await ccService.moveFolder({
                folder_id: action.folderId,
                new_parent_id: action.newParentFolderId,
                new_index: action.newIndex,
            });
            return;
        }
        if (action.kind === "folder-reorder") {
            await ccService.reorderFolder(action.parentFolderId, action.orderedIds);
            return;
        }
        if (action.kind === "ad-link") {
            await ccService.linkAds(organizationId, action.folderId, [action.adId]);
            return;
        }
        await ccService.linkAds(organizationId, action.targetFolderId, [action.adId]);
        await ccService.unlinkAds(organizationId, action.sourceFolderId, [action.adId]);
    }, [ccService, organizationId]);

    const rollbackToCanonicalState = useCallback(async (action: CatalogPersistAction, error: unknown) => {
        console.error("ContentCatalog DnD persist failed", { action, error });
        try {
            await reloadCatalogFromApi();
        } catch (reloadError) {
            if (reloadAbortRef.current?.signal.aborted) return;
            console.error("Failed to reload canonical content catalog state", reloadError);
        }
    }, [reloadCatalogFromApi]);

    // ─── DnD event handlers ───

    const onDragStart = useCallback((event: DragStartEvent) => {
        const dragData = event.active.data.current;
        if (!isCatalogDragItem(dragData)) return;
        const pointer = getPointerPosition(event.activatorEvent);
        if (pointer) setCatalogPointerPosition(pointer);

        if (dragData.kind === "folder") {
            setDragOverlayLabel(`Folder #${dragData.folderId}`);
        } else {
            setDragOverlayLabel(`Ad #${dragData.adId}`);
        }
        setIsDragging(true);
    }, [getPointerPosition]);

    const onDragCancel = useCallback((_event: DragCancelEvent) => {
        setIsDragging(false);
        setDragOverlayLabel("");
        clearCatalogPointerPosition();
    }, []);

    const onDragEnd = useCallback((event: DragEndEvent) => {
        setIsDragging(false);
        setDragOverlayLabel("");
        const action = handleCatalogDragEnd(event);
        if (!action) return;
        void (async () => {
            try {
                await persistCatalogAction(action);
            } catch (error) {
                await rollbackToCanonicalState(action, error);
            }
        })();
        clearCatalogPointerPosition();
    }, [persistCatalogAction, rollbackToCanonicalState]);

    // ─── Catalog mutation handlers (moved from FolderTree) ───

    const handleCreateFolderAt = useCallback(async (parentFolderId: number | null, section: CatalogSection) => {
        if (pendingMutation) return;
        setPendingMutation(true);
        try {
            const created = await ccService.createFolder(organizationId, {
                name: section === "collection" ? "New Collection" : "New Folder",
                type: section === "collection" ? "collection" : "folder",
                parent_id: parentFolderId,
            });
            const newFolder = toFolderModel(created);
            const roots = (section === "collection" ? contentCatalogStore.collections : contentCatalogStore.folders) as ContentCatalogFolder[];
            if (parentFolderId === null) {
                roots.push(newFolder);
            } else {
                const parentNode = findFolderById(roots, parentFolderId);
                if (!parentNode) return;
                parentNode.children = [...(parentNode.children || []), newFolder];
            }
            if (section === "collection") {
                contentCatalogStore.collections = [...roots];
            } else {
                contentCatalogStore.folders = [...roots];
            }
            contentCatalogStore.selectedId = String(newFolder.id);
            contentCatalogStore.pendingScrollFolderId = newFolder.id;
        } catch (error) {
            console.error("Failed to create folder:", error);
        } finally {
            setPendingMutation(false);
        }
    }, [ccService, organizationId, pendingMutation]);

    const handleCreateAdAt = useCallback(async (folderId: number, section: CatalogSection) => {
        if (pendingMutation) return;
        setPendingMutation(true);
        try {
            const created = await ccService.createAd({
                name: "New Ad",
                type: "container",
                subtype: "container",
                is_template: true,
                organization_id: organizationId,
            });
            await ccService.linkAds(organizationId, folderId, [created.id]);
            const newAd = new Ad(created);
            const roots = (section === "collection" ? contentCatalogStore.collections : contentCatalogStore.folders) as ContentCatalogFolder[];
            const folder = findFolderById(roots, folderId);
            if (folder) {
                folder.ads = [...folder.ads, newAd];
                if (section === "collection") {
                    contentCatalogStore.collections = [...roots];
                } else {
                    contentCatalogStore.folders = [...roots];
                }
            }
        } catch (error) {
            console.error("Failed to create ad:", error);
        } finally {
            setPendingMutation(false);
        }
    }, [ccService, organizationId, pendingMutation]);

    const handleDeleteFolder = useCallback(async (folderId: number, section: CatalogSection) => {
        if (pendingMutation) return;
        setPendingMutation(true);
        try {
            await ccService.deleteFolder(organizationId, folderId);
            const roots = (section === "collection"
                ? contentCatalogStore.collections
                : contentCatalogStore.folders) as ContentCatalogFolder[];
            const removed = removeFolderById(roots, folderId);
            if (removed) {
                if (section === "collection") {
                    contentCatalogStore.collections = [...roots];
                } else {
                    contentCatalogStore.folders = [...roots];
                }
            }
            if (contentCatalogStore.selectedId === String(folderId)) {
                contentCatalogStore.selectedId = section === "collection" ? "__collections__" : "__all__";
            }
        } catch (error) {
            console.error("Failed to delete folder:", error);
        } finally {
            setPendingMutation(false);
        }
    }, [ccService, organizationId, pendingMutation]);

    const handleRenameFolder = useCallback((folderId: number, section: CatalogSection) => {
        const roots = (section === "collection"
            ? contentCatalogStore.collections
            : contentCatalogStore.folders) as ContentCatalogFolder[];
        const existingFolder = findFolderById(roots, folderId);
        if (!existingFolder) return;
        setPendingRename({
            kind: "folder",
            folderId,
            section,
            currentName: existingFolder.name || (section === "collection" ? "Collection" : "Folder"),
        });
    }, []);

    const handleRenameAd = useCallback((adId: number) => {
        const existingAd = findAdById(contentCatalogStore.folders as ContentCatalogFolder[], adId)
            || findAdById(contentCatalogStore.collections as ContentCatalogFolder[], adId);
        if (!existingAd) return;
        setPendingRename({
            kind: "ad",
            adId,
            currentName: existingAd.name || `Ad ${adId}`,
        });
    }, []);

    const handleConfirmRename = useCallback(async (nextName: string) => {
        if (!pendingRename || renaming) return;
        setRenaming(true);
        try {
            if (pendingRename.kind === "folder") {
                const roots = (pendingRename.section === "collection"
                    ? contentCatalogStore.collections
                    : contentCatalogStore.folders) as ContentCatalogFolder[];
                await ccService.updateFolder(organizationId, pendingRename.folderId, { name: nextName });
                const renamed = renameFolderInTree(roots, pendingRename.folderId, nextName);
                if (renamed) {
                    if (pendingRename.section === "collection") {
                        contentCatalogStore.collections = [...roots];
                    } else {
                        contentCatalogStore.folders = [...roots];
                    }
                }
            } else {
                await ccService.updateAd(pendingRename.adId, { name: nextName });
                const foldersChanged = renameAdInTree(contentCatalogStore.folders as ContentCatalogFolder[], pendingRename.adId, nextName);
                const collectionsChanged = renameAdInTree(contentCatalogStore.collections as ContentCatalogFolder[], pendingRename.adId, nextName);
                if (foldersChanged) {
                    contentCatalogStore.folders = [...(contentCatalogStore.folders as ContentCatalogFolder[])];
                }
                if (collectionsChanged) {
                    contentCatalogStore.collections = [...(contentCatalogStore.collections as ContentCatalogFolder[])];
                }
            }
            setPendingRename(null);
        } catch (error) {
            console.error("Failed to rename:", error);
        } finally {
            setRenaming(false);
        }
    }, [ccService, organizationId, pendingRename, renaming]);

    const handleDeleteAd = useCallback(async (adId: number, parentFolderId: number, section: CatalogSection) => {
        try {
            if (section === "collection") {
                await ccService.unlinkAds(organizationId, parentFolderId, [adId]);
                const removedFromCollection = removeAdFromFolder(
                    contentCatalogStore.collections as ContentCatalogFolder[],
                    parentFolderId,
                    adId,
                );
                if (removedFromCollection) {
                    contentCatalogStore.collections = [...(contentCatalogStore.collections as ContentCatalogFolder[])];
                }
                return;
            }
            await ccService.deleteAd(adId);
            const removedFromAll = removeAdFromTree(contentCatalogStore.folders as ContentCatalogFolder[], adId);
            const removedFromCollections = removeAdFromTree(contentCatalogStore.collections as ContentCatalogFolder[], adId);
            if (removedFromAll) {
                contentCatalogStore.folders = [...(contentCatalogStore.folders as ContentCatalogFolder[])];
            }
            if (removedFromCollections) {
                contentCatalogStore.collections = [...(contentCatalogStore.collections as ContentCatalogFolder[])];
            }
        } catch (error) {
            console.error("Failed to delete ad:", error);
        }
    }, [ccService, organizationId]);

    const handleConfirmDelete = useCallback(() => {
        if (!pendingDelete) return;
        if (pendingDelete.kind === "folder") {
            void handleDeleteFolder(pendingDelete.folderId, pendingDelete.section);
        } else {
            void handleDeleteAd(pendingDelete.adId, pendingDelete.parentFolderId, pendingDelete.section);
        }
        setPendingDelete(null);
    }, [pendingDelete, handleDeleteFolder, handleDeleteAd]);

    // ─── Ad click / editor ───

    const canUseCreateEditor = hasAccess("Create Editor");

    const handleAdClick = useCallback((ad: Ad) => {
        if (!canUseCreateEditor) return;
        setEditingAdId(ad.id);
        setIsEditorOpen(true);
        setSearchParams((prev) => { prev.set("adId", String(ad.id)); return prev; }, { replace: true });
    }, [canUseCreateEditor, setSearchParams]);

    const setEditorOpen = useCallback((isOpen: boolean) => {
        setIsEditorOpen(isOpen);
        if (!isOpen) {
            setEditingAdId(null);
            setSearchParams((prev) => { prev.delete("adId"); return prev; }, { replace: true });
        }
    }, [setSearchParams]);

    const onSettingsClick = useCallback(() => {
        setIsSettingsOpen(true);
    }, []);

    // ─── Root-level creation callbacks ───

    const handleCreateRootFolder = useCallback(() => {
        void handleCreateFolderAt(null, "all");
    }, [handleCreateFolderAt]);

    const handleCreateRootCollection = useCallback(() => {
        void handleCreateFolderAt(null, "collection");
    }, [handleCreateFolderAt]);

    // ─── Download ───

    const handleDownloadAd = useCallback(async (ad: Ad) => {
        try {
            const orgName = appStore.loggedInUser?.organization?.name?.replace(/\s+/g, "_") || "org";

            const fullAd = await ccService.getAd(ad.id, ["content", "children"]);
            if (!fullAd) return;

            const leaves = collectLeafAds(fullAd as Ad);
            const leafIds = leaves.map((l) => l.id);
            if (leafIds.length === 0) return;

            const resolved = (await mediaService.resolveAdDisplay(leafIds)).resolved;

            const files = leaves
                .map((l, i) => {
                    const url = resolved[l.id]?.media?.storage_uri;
                    return url ? { url, name: l.name || `${fullAd.name || "asset"}-${i + 1}` } : null;
                })
                .filter((f): f is { url: string; name: string } => f !== null);

            const content = fullAd.content as any;
            const bodyCopy = content?.copy_text ?? "—";
            const linkHeadline = content?.link_headline ?? "—";
            const linkDesc = content?.link_description ?? "—";

            const copyTxt = [
                `Body Copy: ${bodyCopy}`,
                `Link Headline: ${linkHeadline}`,
                `Link Description: ${linkDesc}`,
            ].join("\r\n\r\n");

            await zipAndDownload(files, `${orgName}_${fullAd.id}_media`, {
                "copy.txt": copyTxt,
            });
        } catch (err) {
            console.error("Download failed:", err);
        }
    }, [ccService, mediaService]);

    // ─── Menu builder fns ───

    const folderMenuItems: FolderMenuItemsFn = useCallback((folder, section) => [
        ...(canCreate ? [{ label: "New Folder", onSelect: () => { void handleCreateFolderAt(folder.id, section); } }] : []),
        ...(canCreate && hasAccess("Create Editor") ? [{ label: "New Ad", onSelect: () => { void handleCreateAdAt(folder.id, section); } }] : []),
        ...(canUpdate ? [{ label: "Rename Folder", onSelect: () => handleRenameFolder(folder.id, section) }] : []),
        ...(canDelete ? [{
            label: "Delete Folder",
            onSelect: () => setPendingDelete({ kind: "folder", folderId: folder.id, section, name: folder.name }),
            danger: true,
        }] : []),
    ], [handleCreateFolderAt, handleCreateAdAt, handleRenameFolder, canCreate, canUpdate, canDelete]);

    const adMenuItems: AdMenuItemsFn = useCallback((ad, parentFolderId, section) => [
        { label: "Download Content", onSelect: () => { void handleDownloadAd(ad); } },
        ...(canUpdate && hasAccess("Create Editor") ? [{ label: "Edit Ad", onSelect: () => handleAdClick(ad) }] : []),
        ...(canUpdate ? [{ label: "Rename Ad", onSelect: () => handleRenameAd(ad.id) }] : []),
        ...(canDelete ? [{
            label: section === "collection" ? "Remove Ad" : "Delete Ad",
            onSelect: () => setPendingDelete({
                kind: "ad",
                adId: ad.id,
                parentFolderId,
                section,
                name: ad.name || `Ad ${ad.id}`,
            }),
            danger: true,
        }] : []),
    ], [handleAdClick, handleRenameAd, handleDownloadAd, canUpdate, canDelete]);

    const collectionMenuItems: FolderMenuItemsFn = useCallback((folder, section) => [
        ...(canCreate ? [{ label: "New Collection", onSelect: () => { void handleCreateFolderAt(folder.id, section); } }] : []),
        ...(canCreate && hasAccess("Create Editor") ? [{ label: "New Ad", onSelect: () => { void handleCreateAdAt(folder.id, section); } }] : []),
        ...(canUpdate ? [{ label: "Rename Collection", onSelect: () => handleRenameFolder(folder.id, section) }] : []),
        ...(canDelete ? [{
            label: "Delete Collection",
            onSelect: () => setPendingDelete({ kind: "folder", folderId: folder.id, section, name: folder.name }),
            danger: true,
        }] : []),
    ], [handleCreateFolderAt, handleCreateAdAt, handleRenameFolder, canCreate, canUpdate, canDelete]);

    const collectionAdMenuItems: AdMenuItemsFn = useCallback((ad, parentFolderId, section) => [
        { label: "Download Content", onSelect: () => { void handleDownloadAd(ad); } },
        ...(canUpdate && hasAccess("Create Editor") ? [{ label: "Edit Ad", onSelect: () => handleAdClick(ad) }] : []),
        ...(canUpdate ? [{ label: "Rename Ad", onSelect: () => handleRenameAd(ad.id) }] : []),
        ...(canDelete ? [{
            label: "Remove Ad",
            onSelect: () => setPendingDelete({
                kind: "ad",
                adId: ad.id,
                parentFolderId,
                section,
                name: ad.name || `Ad ${ad.id}`,
            }),
            danger: true,
        }] : []),
    ], [handleAdClick, handleRenameAd, handleDownloadAd, canUpdate, canDelete]);

    // ─── Delete modal text ───

    const deleteTitle = pendingDelete
        ? pendingDelete.kind === "folder"
            ? (pendingDelete.section === "collection" ? "Delete Collection" : "Delete Folder")
            : (pendingDelete.section === "collection" ? "Remove Ad" : "Delete Ad")
        : "";

    const deleteMessage = pendingDelete
        ? pendingDelete.kind === "folder"
            ? `Delete "${pendingDelete.name}"?`
            : pendingDelete.section === "collection"
                ? `Remove "${pendingDelete.name}" from this collection?`
                : `Delete "${pendingDelete.name}"?`
        : "";

    const deleteConfirmLabel = pendingDelete
        ? pendingDelete.kind === "ad" && pendingDelete.section === "collection"
            ? "Remove"
            : "Delete"
        : "Delete";

    return (
        <div className="p-3 h-100">
            <Card className="custom-card h-100 d-flex flex-column">
                <Card.Header className="card-header flex-shrink-0 d-flex align-items-center justify-content-between gap-2">
                    <Card.Title className="mb-0">Content Catalog</Card.Title>
                    <div className="d-flex align-items-center gap-2">
                        {canUseCatalogSelector && (
                            <div className="catalog-org-picker">
                                <OrganizationPicker
                                    value={organizationId}
                                    onChange={(id) => { if (id != null) setOrganizationId(id); }}
                                    placeholder="Select organization..."
                                />
                            </div>
                        )}
                        {hasAccess("Store Settings") && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={onSettingsClick}
                            >
                                Settings
                            </button>
                        )}
                    </div>
                </Card.Header>
                <Card.Body className="p-0 flex-grow-1" style={{ minHeight: 0, overflow: "hidden" }}>
                    <DndContext
                        autoScroll={{ layoutShiftCompensation: false }}
                        sensors={sensors}
                        collisionDetection={collisionDetection}
                        onDragStart={onDragStart}
                        onDragCancel={onDragCancel}
                        onDragEnd={onDragEnd}
                    >
                        <ContentCatalog
                            organizationId={organizationId}
                            enableDnd={true}
                            isDragging={isDragging}
                            onAdClick={handleAdClick}
                            adMenuItems={adMenuItems}
                            folderMenuItems={folderMenuItems}
                            collectionMenuItems={collectionMenuItems}
                            collectionAdMenuItems={collectionAdMenuItems}
                            onCreateRootFolder={handleCreateRootFolder}
                            onCreateRootCollection={handleCreateRootCollection}
                        />
                        <DragOverlay>
                            {isDragging && dragOverlayLabel ? (
                                <div className="cc4-drag-overlay">{dragOverlayLabel}</div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                    {editingAdId !== null && hasAccess("Create Editor") && (
                        <CreateEditorModal
                            adId={editingAdId}
                            modalOpen={isEditorOpen}
                            setModalOpen={setEditorOpen}
                            brandAssets={brandAssets}
                            organizationId={organizationId}
                        />
                    )}
                    <StoreSettingsModal
                        isOpen={isSettingsOpen}
                        setIsOpen={setIsSettingsOpen}
                    />
                    <RenameActionModal
                        show={pendingRename !== null}
                        title={pendingRename?.kind === "folder" ? "Rename Folder" : "Rename Ad"}
                        initialValue={pendingRename?.currentName || ""}
                        confirmLabel="Rename"
                        onConfirm={(nextName) => { void handleConfirmRename(nextName); }}
                        onClose={() => { if (!renaming) setPendingRename(null); }}
                        isLoading={renaming}
                    />
                    <ConfirmActionModal
                        show={pendingDelete !== null}
                        title={deleteTitle}
                        message={deleteMessage}
                        confirmLabel={deleteConfirmLabel}
                        onConfirm={handleConfirmDelete}
                        onClose={() => setPendingDelete(null)}
                    />
                </Card.Body>
            </Card>
        </div>
    );
}
